import { gql } from '@apollo/client';

/**
 * Voice and style checks as the app says them (backlog 1c4851c2). The post
 * panel, Approvals, Calendar, Posts and the campaign line all read one stored
 * result (apiserver reach/services/voiceChecks.js) through these words, so they
 * can't disagree.
 */

export const VOICE_PATH = '/settings/voice';
export const PLANS_PATH = '/settings/billing';

export interface VoiceFinding {
  kind: 'voice' | 'style' | 'spelling';
  message: string;
  severity: 'warning' | 'suggestion' | 'error';
  quote?: string | null;
}

export interface VoiceCheck {
  /** findings · clean · pending (being read) · none (no voice set) · pro (lower plan) */
  state: 'findings' | 'clean' | 'pending' | 'none' | 'pro';
  offVoice: boolean;
  holds: boolean;
  kept?: boolean;
  findings: VoiceFinding[];
  rewrite?: string | null;
  overLimit?: boolean;
}

export type VoiceKind = 'post' | 'plan_post' | 'campaign_brief';

export interface VoiceRow {
  icon: string;
  color: string;
  text: string;
  sev: string;
  /** A link beside the text ("No voice set · Set one"). */
  set?: boolean;
}

const GREEN = 'var(--ink-green-ink)';
const AMBER = 'var(--ink-amber)';
const GREY = 'var(--ink-ink3)';
const RED = 'var(--ink-red)';

const KIND_WORD = { voice: 'Voice', style: 'Style guide', spelling: 'Spelling' };

/** One finding's row; `prefix` names the network when a card has several posts. */
export function findingRow(f: VoiceFinding, holds: boolean, prefix = ''): VoiceRow {
  const text = `${prefix}${KIND_WORD[f.kind] || 'Voice'}: ${f.message}`;
  if (f.severity === 'error') {
    return { icon: '✕', color: RED, text, sev: holds ? 'Holds the post' : 'Error' };
  }
  if (f.severity === 'suggestion') return { icon: '●', color: GREY, text, sev: 'Suggestion' };
  return { icon: '●', color: AMBER, text, sev: 'Warning' };
}

/** The post panel's rows for a check. */
export function checkRows(check: VoiceCheck): VoiceRow[] {
  if (check.state === 'none') {
    return [{ icon: '●', color: GREY, text: 'No voice set', sev: '', set: true }];
  }
  if (check.state === 'pending') {
    return [{ icon: '●', color: GREY, text: 'Checking against your voice…', sev: '' }];
  }
  if (check.kept) {
    return [{ icon: '●', color: GREY, text: 'Off voice. You kept your wording.', sev: 'Kept' }];
  }
  if (check.state === 'clean') {
    return [
      { icon: '✓', color: GREEN, text: 'In your voice', sev: '' },
      { icon: '✓', color: GREEN, text: 'Style guide and spelling followed', sev: '' },
    ];
  }
  return check.findings.map((f) => findingRow(f, check.holds));
}

/**
 * Item checks before a post goes out (backlog e39b634d; apiserver
 * reach/services/postItemChecks.js). They're the server's words, and never the
 * text they found. Text that looks like a password or key holds the post; an
 * http:// link is a warning.
 */
export interface ItemFinding {
  kind: 'secret' | 'insecure_link';
  severity: 'hold' | 'approved' | 'warning';
  message: string;
}

export interface ItemCheck {
  holds: boolean;
  approvedAnyway: boolean;
  findings: ItemFinding[];
}

export const KEY_HELD_TOAST =
  'It has text that looks like a password or key, so it waits for a fix or an owner’s approval.';

/** The panel's rows for item checks, what they found first. */
export function itemRows(check: ItemCheck): VoiceRow[] {
  return check.findings.map((f) =>
    f.severity === 'hold'
      ? { icon: '✕', color: RED, text: f.message, sev: 'Holds the post' }
      : f.severity === 'approved'
        ? { icon: '●', color: GREY, text: f.message, sev: 'Approved anyway' }
        : { icon: '●', color: AMBER, text: f.message, sev: 'Warning' }
  );
}

/**
 * What passed, as one line: an item never lists what was checked. Only what
 * ran on a post (backlog 2de6356f), and not what these rows just flagged.
 */
export function foldRow(check: ItemCheck): VoiceRow | null {
  const secret = check.findings.some((f) => f.kind === 'secret');
  const links = check.findings.some((f) => f.kind === 'insecure_link');
  if (secret && links) return null;
  const passed = secret ? 'links' : links ? 'passwords and keys' : 'passwords, keys and links';
  return { icon: '✓', color: GREEN, text: `Everything else checked: ${passed}.`, sev: '' };
}

/** A warning for a card's check line and preview: "A link or image uses an unsafe address." */
export const itemWarning = (check: ItemCheck | null | undefined) =>
  check?.findings.find((f) => f.severity === 'warning')?.message || null;

/** Try it's rows: there, an error is just an error. */
export const tryRows = (findings: VoiceFinding[]) => findings.map((f) => findingRow(f, false));

/** Short findings for the line under a preview card: "Off voice: three exclamation marks". */
export function offVoiceLine(check: VoiceCheck): string {
  const words = check.findings.map((f) => f.message.replace(/[.\s]+$/, '')).slice(0, 3);
  return `Off voice: ${words.join(', ')}`;
}

export interface VoiceSubject {
  kind: VoiceKind;
  id: string;
  /** The network's name, shown when a card has several posts. */
  network?: string;
  check?: VoiceCheck | null;
}

/** What a card in Approvals says about the posts in it. */
export interface CardVoice {
  off: VoiceSubject[];
  held: VoiceSubject[];
  clear: VoiceSubject[];
  check: string | null;
  rows: VoiceRow[];
  effect: string | null;
  /** The button when some are held. Null: the card's own words stand. */
  cta: string | null;
  allHeld: boolean;
}

export function cardVoice(subjects: VoiceSubject[]): CardVoice {
  const off = subjects.filter((s) => s.check?.offVoice);
  const held = subjects.filter((s) => s.check?.holds);
  const clear = subjects.filter((s) => !s.check?.holds);
  const many = subjects.length > 1;
  const rows = off.flatMap((s) =>
    (s.check?.findings || []).map((f) =>
      findingRow(f, Boolean(s.check?.holds), many && s.network ? `${s.network}: ` : '')
    )
  );
  const allHeld = held.length > 0 && held.length === subjects.length;
  return {
    off,
    held,
    clear,
    check: off.length
      ? many
        ? `● ${off.length} of ${subjects.length} posts ${off.length === 1 ? 'is' : 'are'} off voice`
        : '● This post is off voice'
      : null,
    rows,
    effect: !off.length
      ? null
      : held.length
        ? `${
            held.length === 1
              ? 'One post has a style-guide error, so it waits for a fix.'
              : `${held.length} posts have style-guide errors, so they wait for a fix.`
          } Voice findings are warnings.`
        : 'Voice findings are warnings. You can approve as is, or rewrite first.',
    cta: !held.length
      ? null
      : allHeld
        ? 'Rewrite in your voice'
        : `Approve ${subjects.length - held.length}, hold ${held.length}`,
    allHeld,
  };
}

/** "✓ Brief in your voice · all 4 posts too" / "● Brief in your voice · 1 of 4 posts off voice". */
export function campaignVoiceLine(
  brief: VoiceCheck | null | undefined,
  posts: Array<VoiceCheck | null | undefined>
): { text: string; warn: boolean } | null {
  const checks = posts.filter(Boolean) as VoiceCheck[];
  const known = checks.filter((c) => c.state === 'clean' || c.state === 'findings');
  if (brief?.state !== 'clean' && brief?.state !== 'findings' && !known.length) return null;
  const off = known.filter((c) => c.offVoice).length;
  const briefOff = Boolean(brief?.offVoice);
  const head = briefOff
    ? '● Brief off voice'
    : off
      ? '● Brief in your voice'
      : '✓ Brief in your voice';
  const tail = !known.length
    ? ''
    : off
      ? ` · ${off} of ${known.length} posts off voice`
      : ` · ${known.length === 1 ? 'its post too' : `all ${known.length} posts too`}`;
  return { text: `${head}${tail}`, warn: briefOff || off > 0 };
}

/** The toast when a rewrite can't be saved (over a network's limit, or a plan's post). */
export function rewriteRefused(results: RewriteResult[]): string | null {
  const over = results.find((r) => r.reason === 'over_limit');
  if (over) {
    return `The rewrite is over ${over.limit} characters on this network, so it wasn’t saved. Edit it to fit.`;
  }
  const other = results.find((r) => !r.applied && r.reason && r.reason !== 'no_rewrite');
  return other?.reason || null;
}

export interface RewriteResult {
  kind: VoiceKind;
  id: string;
  applied: boolean;
  previousText?: string;
  reason?: string;
  limit?: number;
}

export const APPLY_VOICE_REWRITES = gql`
  mutation ApplyVoiceRewrites($items: [VoiceItemInput!]!) {
    applyVoiceRewrites(items: $items)
  }
`;

export const UNDO_VOICE_REWRITES = gql`
  mutation UndoVoiceRewrites($items: [VoiceUndoInput!]!) {
    undoVoiceRewrites(items: $items)
  }
`;

export const KEEP_VOICE_WORDING = gql`
  mutation KeepVoiceWording($kind: String!, $id: ID!) {
    keepVoiceWording(kind: $kind, id: $id)
  }
`;

/** The panel's own read: a check still running shows up without reopening. */
export const SOCIAL_POST_CHECK = gql`
  query SocialPostCheck($id: ID!) {
    socialPost(id: $id) {
      id
      voiceCheck
      itemCheck
    }
  }
`;

/** The Campaign view's line: the brief and every post's result. */
export const CAMPAIGN_VOICE = gql`
  query CampaignVoice($id: ID!) {
    project(id: $id) {
      id
      briefCheck
      posts {
        id
        status
        voiceCheck
        targets {
          platform
        }
      }
      recentPosts {
        id
        reachStatus
        voiceCheck
      }
    }
  }
`;

const VOICE_FIELDS = `
  saved
  tone
  voiceSeverity
  networkNotes
  styleRules
  dialect
  acceptedWords
  updatedAt
  canManage
  copyEditRequired
  socialCopyEdit
  campaignCopyEdit
  socialChecks
`;

export const ACCOUNT_VOICE = gql`
  query AccountVoice($accountId: ID!) {
    accountVoice(accountId: $accountId) { ${VOICE_FIELDS} }
  }
`;

export const UPDATE_ACCOUNT_VOICE = gql`
  mutation UpdateAccountVoice($accountId: ID!, $input: AccountVoiceInput!) {
    updateAccountVoice(accountId: $accountId, input: $input) { ${VOICE_FIELDS} }
  }
`;

export const TRY_VOICE_CHECK = gql`
  mutation TryVoiceCheck($accountId: ID!, $text: String!) {
    tryVoiceCheck(accountId: $accountId, text: $text)
  }
`;

export interface AccountVoice {
  saved: boolean;
  tone: string;
  voiceSeverity: 'warn' | 'note';
  networkNotes: Record<string, string>;
  styleRules: string[];
  dialect: string;
  acceptedWords: string[];
  updatedAt?: string | null;
  canManage: boolean;
  copyEditRequired: boolean;
  socialCopyEdit: boolean;
  campaignCopyEdit?: boolean;
  socialChecks: boolean;
}

export const DIALECTS = ['US English', 'UK English', 'Canadian English', 'Australian English'];

/** The Website card's one line: "4 rules · US English · Plain, direct, a little dry…". */
export function voiceSummary(v: Pick<AccountVoice, 'styleRules' | 'dialect' | 'tone'>): string {
  const n = v.styleRules.length;
  const tone = v.tone.trim().replace(/\s+/g, ' ');
  return [
    `${n} ${n === 1 ? 'rule' : 'rules'}`,
    v.dialect,
    ...(tone ? [tone.length > 64 ? `${tone.slice(0, 64).trimEnd()}…` : tone] : []),
  ].join(' · ');
}
